// FILA DE PEDIDOS DE ACESSO A DENÚNCIA + o ato de autorizar (operador, 2026-08-19 / 23-08).
//
// ONDE ELE MORA: dentro da `CofrePage`, como "Pedidos a serem liberados" (operador, 23/08).
//
// Não é break-glass — autorizar não toca o escrow. Mas o `RoleGuard` daquela página exigia
// `cofre_holder`/`admin_master`, e o `admin_ti` é obrigatório numa das duas cadeiras, então
// ele TEM de alcançá-la. O guard passou a aceitá-lo, e a PÁGINA se adapta ao papel: quem não
// é `cofre_holder`/`admin_master` vê só os pedidos, sem o break-glass que o servidor
// recusaria para ele (403) — com um aviso dizendo por quê, em vez de silêncio. Isso evita o
// "link visível + acesso negado é o pior par possível" que o `App.tsx` condena por escrito.
//
// ⚠️ CONTENT-FREE por construção: o endpoint devolve `caseId`, `categoria` e o solicitante.
// Nada do teor — `admin_ti` não pode ler conteúdo (ADR-0002).

import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button } from '@aions/ui-primitives';
import { apiFetch } from '../lib/api-client';

export interface SolicitacaoItem {
  caseId: string;
  categoria: string;
  solicitanteId: string;
  solicitanteEmail: string | null;
  rodada: string;
  autorizada: boolean;
  /** Quem já aprovou, por e-mail. SEM rótulo de cadeira — ver abaixo. */
  aprovaramEmails: string[];
  /** Cadeiras que, numa SEGUNDA conta, fechariam o par. Vazio = já fechado OU faltam duas. */
  cadeirasQueFecham: string[];
  /**
   * 🔴 O SERVIDOR decide se EU posso autorizar. A tela obedece.
   *
   * A versão anterior recebia `faltaAdminTi`/`faltaAdminMaster` e cruzava com os próprios
   * papéis aqui. Era a regra em DOIS lugares, e a cópia daqui errava: uma conta com os dois
   * papéis (o `gestor.master`, ADR-0022) não "falta" numa cadeira específica — serve em
   * qualquer uma. O resultado medido foi DEADLOCK: o gestor aprovava, era rotulado como
   * T.I., e então nem ele nem o admin_ti puro tinham botão. Relato do operador em 23/08.
   */
  euPossoAutorizar: boolean;
  euJaAprovei: boolean;
  /**
   * Ninguém vivo consegue re-embrulhar a chave deste caso.
   *
   * INFORMATIVO: diz que o caminho barato (um leitor abre o caso e a chave vai sozinha) não
   * existe aqui — é o caso encerrado com o comitê inteiro trocado. A tela usa isto para
   * oferecer a entrega pelo escrow em destaque, não para permiti-la (o servidor não exige
   * orfandade; ver o 🔴 do endpoint `entregar-chave`).
   */
  semPortadorVivo: boolean;
}

/**
 * 🔴 O ERRO DO SERVIDOR, e não "erro inesperado".
 *
 * Mesma lição que a `CofrePage` aprendeu com o relato de 19/08 ("erro inesperado. Tente
 * novamente" instrui a repetir exatamente o que não vai funcionar). Códigos conhecidos deste
 * endpoint ganham copy própria; o resto cai no genérico **com o código anexado**, para o
 * desconhecido ser investigável em vez de mudo.
 */
async function erroDaAutorizacao(
  resp: Response,
  t: ReturnType<typeof useTranslation>['t'],
): Promise<string> {
  let code = '';
  let reason: string | undefined;
  try {
    const b = (await resp.json()) as { error?: string; reason?: string };
    code = b.error ?? '';
    reason = b.reason;
  } catch {
    // corpo não-JSON — cai no genérico com o status.
  }
  // 🔴 `reason` VEM PRIMEIRO. Os 403 da entrega chegam como
  // `{error:'forbidden', reason:'denunciado_no_proprio_caso'}` — ler só o `error` daria a copy
  // genérica ("sua conta não pode autorizar"), que é o defeito que esta função existe para
  // consertar. Mesma lição do `erroDoServidor` da `CofrePage`.
  const cod = reason ?? code;
  const mapa: Record<string, string> = {
    sem_solicitacao_aberta: 'erro_sem_pedido',
    not_found: 'erro_caso_inexistente',
    nao_autorizada: 'erro_nao_autorizada',
    totp_invalido: 'erro_totp_invalido',
    alvo_inelegivel: 'erro_alvo_inelegivel',
    denunciado_no_proprio_caso: 'erro_denunciado',
    alta_direcao: 'erro_alta_direcao',
    case_purged: 'erro_case_purged',
    cofre_misconfigured: 'erro_cofre_misconfigured',
    nao_entrega_para_si: 'erro_nao_entrega_para_si',
    nao_autoriza_o_proprio_pedido: 'erro_nao_entrega_para_si',
    falha_entrega: 'erro_falha_entrega',
  };
  const chave = mapa[cod];
  if (chave !== undefined) return t(`cofre.acesso.${chave}`);
  if (cod === 'forbidden' || resp.status === 403) return t('cofre.acesso.erro_sem_permissao');
  return t('common.error_unknown') + ` (${cod || String(resp.status)})`;
}

export function SolicitacoesAcessoPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const [itens, setItens] = useState<SolicitacaoItem[]>([]);
  const [carregou, setCarregou] = useState(false);
  /** Estado POR ITEM — chave `caseId::solicitanteId`. Um estado global faria o spinner de um
   *  pedido aparecer em todos, e o erro de um contaminar os outros. */
  const [ocupado, setOcupado] = useState<Record<string, boolean>>({});
  const [erro, setErro] = useState<Record<string, string>>({});
  /** TOTP digitado POR ITEM — nunca um campo global: dois pedidos abertos usariam o mesmo
   *  código e o segundo falharia sem explicação. */
  const [totpPor, setTotpPor] = useState<Record<string, string>>({});

  const chave = (s: { caseId: string; solicitanteId: string }): string =>
    `${s.caseId}::${s.solicitanteId}`;

  const carregar = useCallback(async (): Promise<void> => {
    try {
      const r = await apiFetch('/api/comite/solicitacoes-acesso');
      if (!r.ok) return;
      const b = (await r.json()) as { solicitacoes?: SolicitacaoItem[] };
      setItens(b.solicitacoes ?? []);
    } catch {
      // auxiliar — fila vazia degrada para "nenhuma pendente".
    } finally {
      setCarregou(true);
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Autoriza (ou nega) UMA solicitação.
   *
   * Recarrega a fila em vez de mexer no item local: o veredito das duas cadeiras é do
   * servidor, e recalculá-lo aqui seria a segunda cópia da regra (§CLAUDE.md — contar as
   * cópias antes de duplicar). Se esta aprovação fechou o par, o item volta `autorizada`.
   */
  async function decidir(s: SolicitacaoItem, decisao: 'autorizar' | 'negar'): Promise<void> {
    const k = chave(s);
    // 🔴 SEGUNDO FATOR TAMBÉM AQUI (operador, 24/08). Validação local primeiro: código
    // malformado não gasta chamada nem confunde o erro do servidor com erro de digitação.
    const totp = totpPor[k] ?? '';
    if (!/^\d{6}$/.test(totp)) {
      setErro((e) => ({ ...e, [k]: t('cofre.acesso.erro_totp_formato') }));
      return;
    }
    setOcupado((o) => ({ ...o, [k]: true }));
    setErro((e) => {
      const resto = { ...e };
      delete resto[k];
      return resto;
    });
    try {
      const r = await apiFetch(
        `/api/comite/solicitacoes-acesso/${encodeURIComponent(s.solicitanteId)}/${decisao}`,
        { method: 'POST', body: JSON.stringify({ caseId: s.caseId, totpCode: totp }) },
      );
      if (!r.ok) {
        const msg = await erroDaAutorizacao(r, t);
        setErro((e) => ({ ...e, [k]: msg }));
        return;
      }
      await carregar();
    } catch {
      setErro((e) => ({ ...e, [k]: t('common.error_network') }));
    } finally {
      setOcupado((o) => ({ ...o, [k]: false }));
    }
  }

  /**
   * ENTREGA A CHAVE PELO ESCROW — sem depender de nenhum membro do comitê.
   *
   * 🔴 Exige TOTP, e o segundo fator é o ponto. As duas autorizações são decisão (reversíveis
   * por `negar`); ISTO move material de chave e abre a custódia única do ADR-0015 fora do
   * quórum 2-de-3. Duas sessões roubadas não bastam — foi achado do ataque adversarial.
   *
   * Existe para o cenário que o caminho automático não cobre: caso ENCERRADO com o comitê
   * inteiro substituído. Ali ninguém vai abrir a denúncia, e ninguém tem a chave.
   */
  async function entregarChave(s: SolicitacaoItem): Promise<void> {
    const k = chave(s);
    const totp = totpPor[k] ?? '';
    if (!/^\d{6}$/.test(totp)) {
      setErro((e) => ({ ...e, [k]: t('cofre.acesso.erro_totp_formato') }));
      return;
    }
    setOcupado((o) => ({ ...o, [k]: true }));
    setErro((e) => {
      const resto = { ...e };
      delete resto[k];
      return resto;
    });
    try {
      const r = await apiFetch(
        `/api/comite/solicitacoes-acesso/${encodeURIComponent(s.solicitanteId)}/entregar-chave`,
        { method: 'POST', body: JSON.stringify({ caseId: s.caseId, totpCode: totp }) },
      );
      if (!r.ok) {
        const msg = await erroDaAutorizacao(r, t);
        setErro((e) => ({ ...e, [k]: msg }));
        return;
      }
      setTotpPor((p) => ({ ...p, [k]: '' }));
      await carregar();
    } catch {
      setErro((e) => ({ ...e, [k]: t('common.error_network') }));
    } finally {
      setOcupado((o) => ({ ...o, [k]: false }));
    }
  }

  // Nada pendente E já carregou → não renderiza. Um bloco "0 pedidos" permanente seria
  // ruído numa tela que já tem três cards de contagem.
  if (carregou && itens.length === 0) return null;

  return (
    <section data-testid="solicitacoes-acesso" className="acesso-secao">
      <h2>{t('cofre.acesso.titulo')}</h2>
      <p className="acesso-explicacao">{t('cofre.acesso.explicacao')}</p>
      <div className="acesso-lista">
        {itens.map((s) => {
          const k = chave(s);
          // 🔴 NENHUMA regra de papel aqui. O servidor já respondeu.
          const podeAgir = s.euPossoAutorizar;
          const rotuloCadeira = (c: string): string =>
            c === 'admin_ti'
              ? t('cofre.acesso.cadeira_admin_ti')
              : t('cofre.acesso.cadeira_admin_master');
          return (
            <article
              key={k}
              // Filete de estado à esquerda, na MESMA convenção do `cofre-card`: verde quando
              // autorizado, âmbar enquanto falta cadeira. Reaproveitar a linguagem visual da
              // página é o ponto — o painel mora no cofre e não deve parecer enxertado.
              className={`cofre-card acesso-card ${s.autorizada ? 'state-concedido' : 'state-solicitado'}`}
              data-testid={`solicitacao-${s.solicitanteId}`}
              aria-label={`Pedido de acesso de ${s.solicitanteEmail ?? s.solicitanteId}`}
            >
              <header>
                <strong>{s.solicitanteEmail ?? s.solicitanteId}</strong>{' '}
                <span className="acesso-selo" data-estado={s.autorizada ? 'liberado' : 'pendente'}>
                  {s.autorizada ? t('cofre.acesso.selo_liberado') : t('cofre.acesso.selo_pendente')}
                </span>
              </header>
              <div className="cofre-card-meta">
                <span>
                  {t('cofre.acesso.pede_acesso_a')} <code>{s.caseId}</code>
                </span>
                <span>{s.categoria}</span>
                {/* QUEM aprovou, sem rótulo de cadeira. Rotular exigiria escolha arbitrária
                    quando uma conta tem os dois papéis — e foi essa escolha que travou a
                    tela. "Aprovaram: X" é verdade; "X é o T.I. deste pedido" não era. */}
                {s.aprovaramEmails.length > 0 && (
                  <span data-testid={`aprovaram-${s.solicitanteId}`}>
                    {t('cofre.acesso.aprovaram')} <strong>{s.aprovaramEmails.join(', ')}</strong>
                  </span>
                )}
              </div>
              {/* 🔴 AUTORIZADO ≠ JÁ LÊ. Sem esta distinção na tela, o admin libera, o
                  solicitante continua sem ver, e o relato vira "liberei e não funcionou". */}
              <p className="acesso-estado">
                {s.autorizada
                  ? t('cofre.acesso.autorizada_aguarda_chave')
                  : s.cadeirasQueFecham.length > 0
                    ? // Falta UMA conta, e estas são as cadeiras que servem. Com o gestor de
                      // dois papéis já dentro, as DUAS servem — daí o "ou".
                      t('cofre.acesso.falta_uma', {
                        cadeiras: s.cadeirasQueFecham.map(rotuloCadeira).join(t('cofre.acesso.ou')),
                      })
                    : t('cofre.acesso.faltam_duas')}
              </p>
              {erro[k] !== undefined && (
                <Alert variant="error" role="alert">
                  {erro[k]}
                </Alert>
              )}
              {podeAgir && (
                <div className="cofre-card-actions">
                  {/* 🔴 O TOTP fica JUNTO dos botões, não num modal. A decisão e o fator são
                      um ato só; separá-los num pop-up faria o admin digitar o código, o
                      modal fechar, e ele não saber se autorizou. */}
                  <label className="acesso-entrega__totp">
                    {t('cofre.acesso.totp_label')}
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={totpPor[k] ?? ''}
                      onChange={(e) =>
                        setTotpPor((p) => ({ ...p, [k]: e.target.value.replace(/\D/g, '') }))
                      }
                      data-testid={`totp-decidir-${s.solicitanteId}`}
                    />
                  </label>
                  <Button
                    onClick={() => void decidir(s, 'autorizar')}
                    disabled={ocupado[k] === true}
                    data-testid={`autorizar-${s.solicitanteId}`}
                  >
                    {ocupado[k] === true ? '…' : t('cofre.acesso.autorizar')}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={() => void decidir(s, 'negar')}
                    disabled={ocupado[k] === true}
                    data-testid={`negar-${s.solicitanteId}`}
                  >
                    {t('cofre.acesso.negar')}
                  </Button>
                </div>
              )}
              {s.euJaAprovei && !s.autorizada && (
                <p className="acesso-ja-aprovou">{t('cofre.acesso.voce_ja_aprovou')}</p>
              )}
              {/* 🔴 ENTREGA PELO ESCROW — só depois de autorizado, e com TOTP.
                  Existe para o cenário que o caminho automático não cobre: caso ENCERRADO com
                  o comitê inteiro substituído, onde ninguém vai abrir a denúncia e ninguém tem
                  a chave. Fica em DESTAQUE quando `semPortadorVivo`, porque aí é o único
                  caminho; nos outros casos continua disponível, mas discreto — o caminho do
                  navegador é mais barato e não toca a custódia única. */}
              {s.autorizada && (
                <div className="acesso-entrega" data-destaque={s.semPortadorVivo}>
                  <p className="acesso-entrega__aviso">
                    {s.semPortadorVivo
                      ? t('cofre.acesso.entrega_obrigatoria')
                      : t('cofre.acesso.entrega_opcional')}
                  </p>
                  <div className="cofre-card-actions">
                    <label className="acesso-entrega__totp">
                      {t('cofre.acesso.totp_label')}
                      <input
                        type="text"
                        inputMode="numeric"
                        maxLength={6}
                        value={totpPor[k] ?? ''}
                        onChange={(e) =>
                          setTotpPor((p) => ({ ...p, [k]: e.target.value.replace(/\D/g, '') }))
                        }
                        data-testid={`totp-${s.solicitanteId}`}
                      />
                    </label>
                    <Button
                      onClick={() => void entregarChave(s)}
                      disabled={ocupado[k] === true}
                      data-testid={`entregar-${s.solicitanteId}`}
                    >
                      {ocupado[k] === true ? '…' : t('cofre.acesso.entregar')}
                    </Button>
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}
